import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const QA_TEST_EMAILS = new Set([
  "parent.beta@heirwise.app",
  "teen.beta@heirwise.app",
]);

function isKnownQaTestEmail(email?: string | null) {
  return Boolean(email && QA_TEST_EMAILS.has(email.trim().toLowerCase()));
}

export function useQaTestAccess() {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isQaTestAllowlisted, setIsQaTestAllowlisted] = useState(false);

  const refetch = useCallback(async () => {
    if (authLoading) {
      setLoading(true);
      return;
    }

    if (!user) {
      setIsQaTestAllowlisted(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    const emailFallback = isKnownQaTestEmail(user.email);

    // These are dedicated non-admin QA accounts. Treat their authenticated
    // session email as an immediate fallback so preview cannot get stuck
    // behind the public beta/signup redirect if the target RPC is delayed or a
    // migration has not finished applying yet.
    if (emailFallback) {
      setIsQaTestAllowlisted(true);
      setLoading(false);
    }

    try {
      const { data, error } = await (supabase).rpc("is_qa_test_allowlisted_self");
      if (error) throw error;
      setIsQaTestAllowlisted(Boolean(data) || emailFallback);
    } catch (err) {
      console.warn("[useQaTestAccess] allowlist check failed", err);
      setIsQaTestAllowlisted(emailFallback);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, [authLoading, user?.id, user?.email]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { isQaTestAllowlisted, loading, refetch };
}